import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, ModalController, ToastController } from '@ionic/angular';
import { debounceTime, map, shareReplay, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { combineLatest } from 'rxjs';

import { AlertService, formDiff, snapshot } from '../../../../global';
import { EntityFormConfig } from '../../service/entity-form-config';
import { AdminEntityService } from '../../service/entity.service';
import { DeletionHookMetadata, FormConfig } from '../../../config';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormField, FormImageField } from '../../../form-controls/form-field';
import { ImageService } from '../../../../global/images/image.service';
import { UploadStateService } from '../../../form-controls/file-upload/upload-state.service';
import { FormComponent } from '../../../form-controls/form/form.component';

/** Recursively collect all image fields from a form definition (traverses sections and groups). */
function collectImageFields(fields: FormField[]): FormImageField[] {
    return fields.reduce((acc, field) => {
        if (field.type === 'image') {
            acc.push(field as FormImageField);
        } else if ('fields' in field && field.type !== 'array') {
            acc.push(...collectImageFields((field as any).fields));
        }
        return acc;
    }, [] as FormImageField[]);
}

@Component({
    standalone: false,
    selector: 'abs-entity-details',
    template: `
        <abs-page-layout
            *ngIf="entity$ | async as entity"
            [pageTitle]="entity.name?.en || entity.name || entity.rawValue?.name?.en || entity.rawValue?.name"
        >
            <abs-form
                *ngLet="{ entityTypeMetaData: entityTypeMetaData$ | async } as props"
                [config]="this.formConfig$ | async"
                [value]="entity$ | async"
                (formSubmit)="onSubmit($event)"
            >
                <div class="floatingButtons">
                    <ion-button size="default" color="danger" (click)="deleteEntity(entity)"
                        >Delete {{ props.entityTypeMetaData.entityName }}</ion-button
                    >
                    <ion-button size="default" color="warning" (click)="duplicateEntity(entity)"
                        >Duplicate {{ props.entityTypeMetaData.entityName }}</ion-button
                    >
                    <div>
                        <ion-button size="default" type="submit" [disabled]="uploadState.uploading()" color="success">Save</ion-button>
                        <ion-button size="default" (click)="promote()" *ngIf="env() === 'staging'" [disabled]="uploadState.uploading()">
                            Promote
                        </ion-button>
                        <button style="display:none" #promoteBtn type="submit" name="promoteBtn">Promote</button>
                    </div>
                </div>
            </abs-form>
        </abs-page-layout>
    `,
    styles: [
        `
            abs-form {
                margin-bottom: 50px;
                padding-bottom: 100px;
                display: block;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EntityDetailsComponent {
    formConfig$ = this.route.params.pipe(
        switchMap((p) => {
            const form = this.entityFormConfig.games[p.gameId].forms[p.entityType];
            return form;
        }),
        map((config) => {
            if (config.formDefinition) {
                return config;
            }

            console.log('Form config change');

            return {
                ...config,
                raw: true,
                formDefinition: [
                    {
                        type: 'json',
                        name: 'rawValue'
                    }
                ]
            } as FormConfig;
        })
    );

    env = this.entityService.env;

    entityTypeMetaData$ = this.route.params.pipe(map((p) => this.entityFormConfig.games[p.gameId].entityTypeMetaData[p.entityType]));

    entity$ = combineLatest([this.entityService.getEntityByRoute(this.route), this.formConfig$]).pipe(
        debounceTime(1000),
        map(([entity, fc]) => {
            if (fc.raw) {
                return {
                    rawValue: entity
                };
            }

            return entity;
        }),
        debounceTime(100),
        tap((x) => console.log('Form entity change')),
        shareReplay(1)
    );
    entity = toSignal(this.entity$);

    gameId = this.route.snapshot.params.gameId;
    entityType = this.route.snapshot.params.entityType;

    alert = inject(AlertService);

    private skipDirtyCheck = false;

    @ViewChild('promoteBtn')
    promoteBtn: any;

    @ViewChild(FormComponent)
    private formRef!: FormComponent;

    private imageService = inject(ImageService);
    uploadState = inject(UploadStateService);

    constructor(
        public entityService: AdminEntityService,
        private toastController: ToastController,
        private route: ActivatedRoute,
        private alertController: AlertController,
        private modalController: ModalController,
        private router: Router,
        private entityFormConfig: EntityFormConfig
    ) {}

    async canDeactivate(): Promise<boolean> {
        if (this.skipDirtyCheck || !this.formRef?.formGroup?.dirty) {
            return true;
        }
        const result = await this.alert.showConfirm('Unsaved Changes', 'You have unsaved changes. Are you sure you want to leave?', {
            ok: 'Leave',
            cancel: 'Stay'
        });
        return result.role === 'ok';
    }

    promote() {
        this.promoteBtn.nativeElement.click();
    }

    async onSubmit(e) {
        const value = e.formValue;
        const promote = e.originalEvent?.submitter?.name === 'promoteBtn';

        if (promote) {
            const res = await this.alert.showConfirm('Are you sure?', 'Are you sure you want to promote this entity?', {
                ok: 'Yes',
                cancel: 'No'
            });
            if (res.role === 'cancel') {
                return;
            }
        }

        snapshot(combineLatest([this.formConfig$, this.entity$]), ([fc, entity]) => {
            let output = value;
            let input = entity;

            if (fc.raw) {
                output = typeof value.rawValue === 'string' ? JSON.parse(value.rawValue) : value.rawValue;
                input = entity.rawValue;
            }
            console.log('Entity from form:', output);

            if (fc.dryRun) {
                const diffResult = formDiff(output, input);
                console.log('-----------------');
                console.log('Form input:', input);
                console.log('Form output:', output);
                console.log('Form data clean?', diffResult.equal);
                if (!diffResult.equal) {
                    console.log('Changed fields:', diffResult.changedFields);
                }
                console.log('Save skipped');
                console.log('-----------------');
            } else {
                this.entityService
                    .saveEntity(output, this.gameId, this.entityType, promote)
                    .toPromise()
                    .then((x) => {
                        console.log({ x });
                        this.presentToast('Save successful!', 'success');
                        this.formRef?.formGroup?.markAsPristine();
                        this.formRef?.formGroup?.markAsUntouched();
                        this.syncImageEntityRefs(fc, input, output);
                    })
                    .catch((err) => this.handleHttpError(err));
            }
        });
    }

    /**
     * After a successful entity save, register/unregister entity refs on any image
     * fields so the file browser shows accurate usage counts.
     */
    private syncImageEntityRefs(fc: FormConfig, oldEntity: any, newEntity: any) {
        const entityId = newEntity._id;
        if (!entityId || !fc.formDefinition) return;

        const imageFields = collectImageFields(fc.formDefinition);
        for (const field of imageFields) {
            const oldVal: string | null = oldEntity?.[field.name] || null;
            const newVal: string | null = newEntity?.[field.name] || null;

            if (oldVal && oldVal !== newVal) {
                this.imageService.removeEntityRef(oldVal, entityId).subscribe({ error: (e) => console.error('removeEntityRef failed', e) });
            }
            if (newVal) {
                this.imageService
                    .addEntityRef(newVal, entityId, this.entityType)
                    .subscribe({ error: (e) => console.error('addEntityRef failed', e) });
            }
        }
    }

    handleHttpError(err) {
        // error
        if (err.status === 403) {
            this.presentToast("You aren't authorized to do that.", 'danger');
        } else {
            this.presentToast('Oops something went wrong.', 'danger');
        }
        console.error(err);
    }

    async presentToast(message, color) {
        const toast = await this.toastController.create({
            message,
            duration: 2000,
            color
        });
        toast.present();
    }

    async deleteEntity(entity: any) {
        snapshot(
            combineLatest([this.formConfig$, this.entity$, this.entityTypeMetaData$]),
            async ([fc, entityData, entityTypeMetaData]) => {
                let resolvedEntity = entityData;
                if (fc.raw) {
                    resolvedEntity = entityData.rawValue;
                }

                const entityId = resolvedEntity._id;
                const entityName = resolvedEntity.name?.en || resolvedEntity.name || resolvedEntity.key;

                // Check for onBeforeDelete hook
                if (fc.onBeforeDelete) {
                    try {
                        const hookMetadata = await fc.onBeforeDelete(this.gameId, this.entityType, entityId, entityName);

                        if (hookMetadata) {
                            // Show the modal provided by the hook
                            this.showDeletionModal(hookMetadata, resolvedEntity, entityTypeMetaData);
                            return;
                        }
                        // hookMetadata is null → no affected forces, proceed with standard deletion
                    } catch (err) {
                        console.error('onBeforeDelete hook failed:', err);
                        this.presentToast('Failed to check for affected forces. Please try again.', 'danger');
                        return;
                    }
                }

                // Standard deletion flow (no hook or no affected forces)
                this.showStandardDeleteConfirm(resolvedEntity, entityTypeMetaData);
            }
        );
    }

    private async showStandardDeleteConfirm(entity: any, entityTypeMetaData: any) {
        const alert = await this.alertController.create({
            header: 'Delete ' + entityTypeMetaData.entityName,
            message: 'Are you sure?  This cannot be undone.',
            buttons: [
                {
                    text: 'No',
                    handler: () => {}
                },
                {
                    text: 'Yes',
                    handler: () => {
                        this.performDeletion(entity);
                    }
                }
            ]
        });
        alert.present();
    }

    private async showDeletionModal(metadata: DeletionHookMetadata, entity: any, entityTypeMetaData: any) {
        const modal = await this.modalController.create({
            component: metadata.modalComponent,
            componentProps: { data: metadata.modalData },
            backdropDismiss: false
        });

        await modal.present();

        const { data, role } = await modal.onDidDismiss();
        if (role === 'confirm') {
            try {
                await metadata.onConfirm(data);
                this.performDeletion(entity);
            } catch (err) {
                console.error('Deletion with alerts failed:', err);
                this.presentToast('Failed to create alerts. Entity was not deleted.', 'danger');
            }
        } else {
            metadata.onCancel();
        }
    }

    private performDeletion(entity: any) {
        this.entityService
            .deleteEntity(entity, entity.gameId, this.route.snapshot.params.entityType)
            .toPromise()
            .then(() => {
                this.skipDirtyCheck = true;
                this.router.navigate(['..'], { relativeTo: this.route });
            })
            .catch((err) => this.handleHttpError(err));
    }

    async duplicateEntity(entity: any) {
        if (this.formRef?.formGroup?.dirty) {
            const result = await this.alert.showConfirm(
                'Unsaved Changes',
                'You have unsaved changes that will not be copied. Continue with duplication?',
                { ok: 'Duplicate', cancel: 'Cancel' }
            );
            if (result.role !== 'ok') {
                return;
            }
        }

        snapshot(this.entityTypeMetaData$, (entityTypeMetaData) => {
            let entityData = entity;
            if (entity.rawValue) {
                entityData = entity.rawValue;
            }

            let name;
            if (typeof entityData.name === 'string') {
                name = entityData.name + ' Copy';
            } else if (typeof entityData.name === 'object') {
                name = {
                    ...entityData.name,
                    en: entityData.name.en + ' Copy'
                };
            }

            const entityCopy = {
                ...entityData,
                name
            };
            delete entityCopy._id;
            delete entityCopy.shortId;
            this.entityService
                .saveEntity(entityCopy, entityData.gameId, this.entityType)
                .toPromise()
                .then((res) => {
                    this.skipDirtyCheck = true;
                    this.router.navigate(['..', res[0]], { relativeTo: this.route });
                    this.presentToast(entityTypeMetaData.entityName + ' Duplicated Successfully', 'success');
                })
                .catch((err) => this.handleHttpError(err));
        });
    }
}
